import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Database, KeyRound, Lock, Mail, ShieldCheck, UserCheck } from "lucide-react";

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const AppNav = () => (
  <header className="sticky top-0 z-50 w-full border-b border-[#E3E6DF] bg-[#FAFAF7]/90 backdrop-blur-xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8">
      <Link to="/" className="flex items-center gap-3" aria-label="KHORN AI home">
        <span className="relative block h-11 w-[172px] shrink-0 overflow-hidden">
          <img src="/logo_and_name.png" alt="KHORN AI" className="absolute -left-[28px] -top-[60px] h-[168px] max-w-none" />
        </span>
      </Link>

      <nav className="hidden items-center gap-8 lg:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="text-xs font-bold uppercase tracking-[0.16em] text-[#6A7168] transition hover:text-[#15130F]">
            {label}
          </a>
        ))}
      </nav>

      <a href="https://app.khorn-ai.com/plan" className="rounded-full bg-[#15130F] px-5 py-3 text-xs font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#1F2A22]">
        เริ่มใช้งาน
      </a>
    </div>
  </header>
);

const highlights = [
  ["ไม่ฝึกโมเดลสาธารณะ", "ไม่ใช้ข้อมูลลูกความ เอกสาร หรือข้อมูลเคสของผู้ใช้ไปฝึกโมเดลสาธารณะ", ShieldCheck],
  ["ผู้ใช้ควบคุมข้อมูล", "ผู้ใช้ควบคุมข้อมูล สิทธิ์การเข้าถึง และคำขอลบหรือแก้ไขข้อมูลได้ตามกฎหมาย", UserCheck],
  ["ประมวลผลอย่างปลอดภัย", "ออกแบบเพื่อรองรับข้อมูลเอกสารกฎหมายและข้อมูลลูกความที่ละเอียดอ่อน", Lock],
];

const sections = [
  {
    title: "1. ผู้ควบคุมข้อมูลส่วนบุคคล",
    body: [
      "ประกาศความเป็นส่วนตัวฉบับนี้จัดทำขึ้นโดย บริษัท ค้อนเอไอ โซลูชั่น จำกัด (\"KHORN AI\", \"เรา\", \"ของเรา\") เพื่ออธิบายการเก็บรวบรวม ใช้ เปิดเผย เก็บรักษา โอน และประมวลผลข้อมูลส่วนบุคคลของผู้ใช้บริการเว็บไซต์ แอปพลิเคชัน ระบบ AI สำหรับสำนักงานกฎหมาย ระบบค้นกฎหมาย ระบบจัดการงานลูกความ และบริการอื่นที่เกี่ยวข้องของ KHORN AI",
      "หากผู้ใช้บริการใช้บริการในนามองค์กร สำนักงานกฎหมาย หรือทีมงาน ผู้ใช้บริการรับทราบว่าองค์กรดังกล่าวอาจเป็นผู้ควบคุมข้อมูลส่วนบุคคลของข้อมูลลูกความหรือข้อมูลบุคคลภายนอกที่นำเข้าสู่ระบบ และ KHORN AI อาจทำหน้าที่เป็นผู้ประมวลผลข้อมูลส่วนบุคคลตามคำสั่งขององค์กรดังกล่าวในบางกรณี",
    ],
  },
  {
    title: "2. ข้อมูลที่เราอาจเก็บรวบรวม",
    body: [
      "ข้อมูลบัญชีและข้อมูลติดต่อ เช่น ชื่อ อีเมล เบอร์โทรศัพท์ ชื่อองค์กร ตำแหน่ง ช่องทางติดต่อ ข้อมูลเข้าสู่ระบบ และข้อมูลที่จำเป็นสำหรับการยืนยันตัวตน",
      "ข้อมูลการชำระเงินและแผนการใช้งาน เช่น ประเภทแพ็กเกจ ประวัติการชำระเงิน ใบเสร็จ ข้อมูลภาษี และข้อมูลที่เกี่ยวข้องกับการให้บริการแบบชำระเงิน โดยข้อมูลบัตรหรือช่องทางชำระเงินอาจถูกประมวลผลโดยผู้ให้บริการชำระเงินภายนอก",
      "ข้อมูลที่ผู้ใช้ป้อน อัปโหลด หรือสร้างในระบบ เช่น ข้อความแชต เอกสาร สัญญา ไฟล์แนบ ข้อมูลเคส ข้อมูลลูกความ หลักฐาน ลำดับเหตุการณ์ งานที่มอบหมาย บันทึกภายใน และคำสั่งที่ส่งให้ AI ประมวลผล",
      "ข้อมูลการใช้งานและข้อมูลทางเทคนิค เช่น หมายเลขไอพี ประเภทอุปกรณ์ บันทึกการใช้งานเบราว์เซอร์ เวลาเข้าใช้งาน URL ที่เรียกใช้ บันทึกข้อขัดข้อง ข้อมูลคุกกี้หรือเทคโนโลยีใกล้เคียง และข้อมูลเชิงสถิติเพื่อปรับปรุงบริการ",
      "ข้อมูลจากบริการภายนอกที่ผู้ใช้เชื่อมต่อ เช่น Google OAuth, email, calendar, file storage, LINE Official Account หรือระบบอื่นที่ผู้ใช้อนุญาตให้เชื่อมต่อ โดยเราจะเข้าถึงเฉพาะขอบเขตที่จำเป็นและตามสิทธิ์ที่ผู้ใช้อนุญาต",
    ],
  },
  {
    title: "3. วัตถุประสงค์ในการใช้ข้อมูล",
    body: [
      "เพื่อให้บริการ KHORN AI เช่น การรับข้อมูลจากช่องทางต่าง ๆ การสรุปข้อมูลเป็นเคส การจัดหลักฐาน การสร้างเอกสาร การค้นฐานกฎหมาย การมอบหมายงาน การติดตามงานค้าง และการแสดงผลใน workspace ของผู้ใช้",
      "เพื่อดูแลบัญชี การยืนยันตัวตน การจัดการสิทธิ์ทีม การเรียกเก็บเงิน การออกใบเสร็จ การให้บริการช่วยเหลือ และการติดต่อเกี่ยวกับบริการ",
      "เพื่อรักษาความปลอดภัย ตรวจจับการใช้งานผิดปกติ ป้องกันการเข้าถึงโดยไม่ได้รับอนุญาต ป้องกันสแปม การฉ้อโกง การดึงข้อมูลโดยไม่ได้รับอนุญาต หรือการละเมิดข้อกำหนดการใช้บริการ",
      "เพื่อปรับปรุงคุณภาพ ความเสถียร ประสิทธิภาพ และประสบการณ์การใช้งานของบริการ โดยอาจใช้ข้อมูลเชิงสถิติหรือข้อมูลที่ทำให้ไม่สามารถระบุตัวบุคคลได้",
      "เพื่อปฏิบัติตามกฎหมาย คำสั่งของหน่วยงานรัฐ กระบวนการยุติธรรม การตรวจสอบภายใน การจัดการข้อพิพาท และการใช้สิทธิเรียกร้องตามกฎหมาย",
    ],
  },
  {
    title: "4. ข้อมูลลูกความ เอกสารคดี และข้อมูลอ่อนไหว",
    body: [
      "เนื่องจากบริการของ KHORN AI อาจถูกใช้กับงานกฎหมาย ผู้ใช้บริการอาจนำข้อมูลลูกความ เอกสารคดี ข้อเท็จจริง หลักฐาน หรือข้อมูลที่มีความละเอียดอ่อนเข้าสู่ระบบ ผู้ใช้บริการมีหน้าที่ตรวจสอบว่ามีสิทธิหรือฐานทางกฎหมายที่เหมาะสมในการนำข้อมูลดังกล่าวมาใช้กับบริการ",
      "เราจะประมวลผลข้อมูลดังกล่าวเพื่อให้บริการตามคำสั่งของผู้ใช้ เช่น สรุป จัดหมวดหมู่ ค้นหา สร้างเอกสาร ตรวจเอกสาร หรือช่วยติดตามงานค้าง และจะไม่ใช้ข้อมูลดังกล่าวเพื่อวัตถุประสงค์อื่นที่ไม่เกี่ยวข้องกับการให้บริการ เว้นแต่ได้รับความยินยอมหรือมีกฎหมายให้อำนาจ",
    ],
  },
  {
    title: "5. นโยบายไม่ใช้ข้อมูลไปฝึกโมเดลสาธารณะ",
    body: [
      "KHORN AI มีนโยบายไม่ใช้ข้อมูลผู้ใช้ ข้อมูลลูกความ เอกสารคดี เอกสารสัญญา คำถาม คำสั่ง หรือผลลัพธ์ที่เกิดจากการใช้งาน ไปฝึกโมเดล AI สาธารณะ",
      "ในกรณีที่บริการต้องใช้ผู้ให้บริการ AI หรือผู้ประมวลผลบนคลาวด์ภายนอกเพื่อประมวลผลคำขอ เราจะเลือกใช้ผู้ให้บริการและการตั้งค่าที่เหมาะสมกับการรักษาความลับและความปลอดภัยของข้อมูลตามที่สามารถทำได้ทางเทคนิคและตามเงื่อนไขของผู้ให้บริการนั้น",
    ],
  },
  {
    title: "6. การเปิดเผยข้อมูลแก่บุคคลภายนอก",
    body: [
      "เราอาจเปิดเผยหรือส่งข้อมูลให้ผู้ให้บริการที่จำเป็นต่อการให้บริการ เช่น โครงสร้างพื้นฐานคลาวด์ ฐานข้อมูล ระบบยืนยันตัวตน ระบบชำระเงิน ระบบวิเคราะห์การใช้งาน อีเมล บริการช่วยเหลือลูกค้า ผู้ประมวลผล AI พื้นที่จัดเก็บข้อมูล ระบบติดตามสถานะ หรือบริการเชื่อมต่อภายนอกที่ผู้ใช้เปิดใช้งาน",
      "เราอาจเปิดเผยข้อมูลเมื่อจำเป็นเพื่อปฏิบัติตามกฎหมาย คำสั่งศาล คำสั่งหน่วยงานรัฐ การบังคับใช้ข้อกำหนด การป้องกันความเสียหาย การตรวจสอบการใช้งานผิดปกติ หรือการคุ้มครองสิทธิของ KHORN AI ผู้ใช้ หรือบุคคลภายนอก",
      "เราไม่ขายข้อมูลส่วนบุคคลของผู้ใช้ให้บุคคลภายนอก",
    ],
  },
  {
    title: "7. การโอนข้อมูลไปต่างประเทศ",
    body: [
      "บริการบางส่วนอาจใช้ผู้ให้บริการคลาวด์ ผู้ประมวลผล AI ผู้ประมวลผลการชำระเงิน หรือผู้ให้บริการอื่นที่ตั้งอยู่ต่างประเทศ หรือมีการประมวลผลข้อมูลนอกประเทศไทย ในกรณีดังกล่าวเราจะดำเนินการตามมาตรการที่เหมาะสมตามกฎหมายคุ้มครองข้อมูลส่วนบุคคลที่เกี่ยวข้อง",
      "ผู้ใช้รับทราบว่าการใช้ฟีเจอร์บางประเภท โดยเฉพาะการประมวลผลด้วย AI บนคลาวด์ หรือการเชื่อมต่อบริการภายนอก อาจทำให้ข้อมูลที่เกี่ยวข้องกับคำขอนั้นถูกส่งหรือประมวลผลนอกประเทศไทย",
    ],
  },
  {
    title: "8. การเก็บรักษาข้อมูล",
    body: [
      "เราจะเก็บรักษาข้อมูลเท่าที่จำเป็นตามวัตถุประสงค์ของการให้บริการ การดูแลบัญชี การปฏิบัติตามกฎหมาย การตรวจสอบข้อพิพาท การรักษาความปลอดภัย และระยะเวลาที่กฎหมายกำหนด",
      "ผู้ใช้สามารถขอลบข้อมูลบางประเภทได้ตามช่องทางที่เรากำหนด ทั้งนี้ การลบบางรายการอาจทำให้ไม่สามารถให้บริการบางส่วนได้ หรืออาจต้องเก็บข้อมูลบางส่วนต่อไปเท่าที่จำเป็นตามกฎหมายหรือเพื่อพิสูจน์สิทธิ",
    ],
  },
  {
    title: "9. คุกกี้และเทคโนโลยีใกล้เคียง",
    body: [
      "เราอาจใช้คุกกี้ พื้นที่จัดเก็บในเครื่อง พิกเซล บันทึกการใช้งาน หรือเทคโนโลยีใกล้เคียงเพื่อให้เว็บไซต์และบริการทำงานได้ เช่น การจดจำเซสชัน การรักษาความปลอดภัย การวิเคราะห์ประสิทธิภาพ และการปรับปรุงประสบการณ์ผู้ใช้",
      "ผู้ใช้สามารถตั้งค่าเบราว์เซอร์เพื่อปฏิเสธคุกกี้บางประเภทได้ แต่อาจทำให้บริการบางส่วนทำงานไม่สมบูรณ์",
    ],
  },
  {
    title: "10. สิทธิของเจ้าของข้อมูล",
    body: [
      "ภายใต้เงื่อนไขของกฎหมายคุ้มครองข้อมูลส่วนบุคคล ผู้ใช้อาจมีสิทธิขอเข้าถึง ขอรับสำเนา ขอแก้ไข ขอให้ลบหรือทำลาย ขอให้ระงับการใช้ ขอคัดค้าน ขอถอนความยินยอม และขอให้โอนย้ายข้อมูลส่วนบุคคลได้ตามที่กฎหมายกำหนด",
      "การใช้สิทธิอาจต้องมีการยืนยันตัวตนและอาจมีข้อจำกัดตามกฎหมาย เช่น กรณีที่เราจำเป็นต้องเก็บข้อมูลเพื่อปฏิบัติตามกฎหมาย การใช้สิทธิเรียกร้อง หรือการรักษาความปลอดภัยของบริการ",
    ],
  },
  {
    title: "11. ความปลอดภัยของข้อมูล",
    body: [
      "เราใช้มาตรการทางเทคนิคและองค์กรตามสมควรเพื่อปกป้องข้อมูล เช่น การเข้ารหัส การควบคุมสิทธิ์ การแยกสภาพแวดล้อม การบันทึกประวัติ การตรวจสอบความผิดปกติ และการจำกัดการเข้าถึงภายในตามความจำเป็น",
      "อย่างไรก็ตาม ไม่มีระบบใดปลอดภัยโดยสมบูรณ์ ผู้ใช้ควรรักษารหัสผ่าน สิทธิ์เข้าถึงบัญชี และการเชื่อมต่อบริการภายนอกของตนเองอย่างระมัดระวัง",
    ],
  },
  {
    title: "12. บริการภายนอกและการเชื่อมต่อ",
    body: [
      "หากผู้ใช้เชื่อมต่อบริการภายนอก เช่น Google, LINE, email, calendar, file storage หรือ plugin ภายนอก การใช้งานดังกล่าวอาจอยู่ภายใต้นโยบายความเป็นส่วนตัวและข้อกำหนดของผู้ให้บริการภายนอกนั้นด้วย",
      "สำหรับ Google API ข้อมูลที่ได้รับจาก Google APIs จะถูกใช้และโอนไปยังแอปอื่นตาม Google API Services User Data Policy รวมถึงข้อกำหนด Limited Use เท่าที่เกี่ยวข้อง",
    ],
  },
  {
    title: "13. Google API Data Use",
    body: [
      "เมื่อผู้ใช้เชื่อมต่อบัญชี Google ผ่าน OAuth เราจะขอและใช้สิทธิ์เฉพาะเท่าที่จำเป็นต่อฟีเจอร์ที่ผู้ใช้เลือกใช้งาน เช่น การเชื่อมต่อ Gmail และ Google Calendar กับ workspace ของ KHORN AI",
      "Gmail read access ใช้เพื่ออ่าน metadata และเนื้อหาอีเมลที่เกี่ยวข้องกับงานคดี งานลูกความ หรือ workspace ของผู้ใช้ เพื่อช่วยรับข้อมูลเข้า สรุป จัดหมวดหมู่ ค้นหา และเชื่อมโยงข้อมูลกับเคสที่เกี่ยวข้อง",
      "สิทธิ์ส่งอีเมลของ Gmail ใช้เพื่อส่งอีเมลจากบัญชีของผู้ใช้เฉพาะเมื่อผู้ใช้สั่งหรืออนุมัติการส่งอย่างชัดเจน เช่น การส่งอีเมลติดตามเอกสาร การส่งสรุปงาน หรือการส่งร่างข้อความที่ผู้ใช้ตรวจแล้ว",
      "สิทธิ์กิจกรรมในปฏิทินใช้เพื่อดู สร้าง หรืออัปเดตกิจกรรมในปฏิทินที่เกี่ยวข้องกับเคส งานนัดหมาย กำหนดเวลา การประชุม หรือการแจ้งเตือนงานของผู้ใช้",
      "KHORN AI จะไม่ลบอีเมลของผู้ใช้ ไม่แก้ไขการตั้งค่า Gmail ไม่เปลี่ยนแปลงการแชร์ calendar และไม่ใช้ข้อมูล Google เพื่อการโฆษณา การขายข้อมูล หรือการฝึกโมเดล AI สาธารณะ",
      "ผู้ใช้สามารถตัดการเชื่อมต่อ Google ภายใน KHORN AI ได้ตามช่องทางที่ระบบจัดให้ และสามารถเพิกถอนสิทธิ์การเข้าถึงของ KHORN AI ได้ทุกเมื่อผ่านหน้าการตั้งค่าบัญชี Google ของผู้ใช้",
    ],
  },
  {
    title: "14. เด็กและผู้เยาว์",
    body: [
      "บริการของ KHORN AI ไม่ได้มุ่งหมายให้ผู้เยาว์ใช้โดยไม่มีความยินยอมจากผู้ปกครอง หากเราทราบว่ามีการเก็บข้อมูลส่วนบุคคลของผู้เยาว์โดยไม่มีฐานทางกฎหมายที่เหมาะสม เราจะดำเนินการตามสมควรเพื่อจัดการหรือลบข้อมูลดังกล่าว",
    ],
  },
  {
    title: "15. การแก้ไขประกาศนี้",
    body: [
      "เราอาจแก้ไขประกาศความเป็นส่วนตัวนี้เป็นครั้งคราวเพื่อให้สอดคล้องกับบริการ เทคโนโลยี กฎหมาย หรือแนวปฏิบัติที่เปลี่ยนแปลง โดยจะแสดงวันที่มีผลบังคับใช้ของฉบับล่าสุดไว้บนหน้านี้",
      "การใช้บริการต่อไปหลังจากมีการปรับปรุงประกาศนี้ให้ถือว่าผู้ใช้รับทราบประกาศฉบับที่ปรับปรุงแล้ว เว้นแต่กฎหมายกำหนดให้ต้องได้รับความยินยอมเพิ่มเติม",
    ],
  },
  {
    title: "16. ช่องทางติดต่อ",
    body: [
      "หากมีคำถามเกี่ยวกับประกาศความเป็นส่วนตัว การใช้สิทธิของเจ้าของข้อมูล หรือการจัดการข้อมูลส่วนบุคคล สามารถติดต่อ บริษัท ค้อนเอไอ โซลูชั่น จำกัด ได้ที่ contact@khorn-ai.com หรือ 081-811-8772",
      "ที่อยู่: 54 ซอย 37 ถนนเฉลิมพระเกียรติร.9 หนองบอน ประเวศ กรุงเทพฯ 10250",
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#15130F] selection:bg-[#15130F] selection:text-white">
      <Helmet>
        <title>ประกาศความเป็นส่วนตัว KHORN AI | Privacy Policy</title>
        <meta
          name="description"
          content="ประกาศความเป็นส่วนตัวของ KHORN AI สำหรับผู้ใช้บริการ AI สำนักงานกฎหมาย ข้อมูลลูกความ เอกสารคดี การประมวลผล AI และสิทธิของเจ้าของข้อมูล"
        />
        <meta name="keywords" content="KHORN AI privacy policy, ประกาศความเป็นส่วนตัว, PDPA, AI สำนักงานกฎหมาย, ข้อมูลลูกความ" />
        <meta property="og:title" content="ประกาศความเป็นส่วนตัว KHORN AI" />
        <meta property="og:description" content="Privacy Policy สำหรับบริการ KHORN AI และข้อมูลสำนักงานกฎหมาย" />
        <meta property="og:url" content="https://www.khorn-ai.com/privacy-policy" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.khorn-ai.com/privacy-policy" />
      </Helmet>

      <AppNav />

      <main>
        <section className="relative overflow-hidden border-b border-[#E3E6DF]">
          <div className="absolute left-1/2 top-0 h-[520px] w-[860px] -translate-x-1/2 rounded-full bg-[#EEF3EA]/70 blur-3xl" />
          <div className="relative mx-auto max-w-7xl px-5 py-20 md:px-8 lg:py-28">
            <div className="max-w-4xl">
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#E3E6DF] bg-[#FFFFFF] px-4 py-2 text-xs font-bold text-[#6A7168] shadow-sm">
                <ShieldCheck size={14} />
                Privacy Policy
              </div>
              <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-7xl">
                ประกาศความเป็นส่วนตัว KHORN AI
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-relaxed text-[#5F665E]">
                เอกสารนี้อธิบายวิธีที่ KHORN AI เก็บรวบรวม ใช้ เปิดเผย เก็บรักษา และประมวลผลข้อมูลส่วนบุคคล
                เมื่อผู้ใช้ใช้งานเว็บไซต์ แอปพลิเคชัน และบริการ AI สำหรับสำนักงานกฎหมาย
              </p>
              <div className="mt-8 text-sm font-semibold text-[#15130F]/52">วันที่มีผลบังคับใช้: 9 มิถุนายน 2569</div>
            </div>
          </div>
        </section>

        <section className="border-b border-[#E3E6DF] bg-[#FFFFFF] py-16">
          <div className="mx-auto grid max-w-7xl gap-4 px-5 md:grid-cols-3 md:px-8">
            {highlights.map(([title, desc, Icon]) => (
              <article key={title} className="rounded-[26px] border border-[#E3E6DF] bg-[#FFFFFF] p-7 shadow-sm">
                <Icon size={24} className="mb-8 text-[#6A7168]" />
                <h2 className="text-2xl font-semibold">{title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-[#5F665E]">{desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:px-8 lg:grid-cols-[280px_1fr] lg:py-24">
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-[24px] border border-[#E3E6DF] bg-[#FFFFFF] p-5">
              <div className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[#6A7168]">สารบัญ</div>
              <nav className="space-y-2">
                {sections.slice(0, 8).map((section) => (
                  <a key={section.title} href={`#${section.title.split(".")[0]}`} className="block rounded-xl px-3 py-2 text-sm font-medium text-[#5F665E] transition hover:bg-[#FFFFFF] hover:text-[#15130F]">
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <article className="space-y-5">
            {sections.map((section) => (
              <section key={section.title} id={section.title.split(".")[0]} className="rounded-[26px] border border-[#E3E6DF] bg-[#FFFFFF] p-7 shadow-sm md:p-9">
                <h2 className="text-2xl font-semibold leading-tight md:text-3xl">{section.title}</h2>
                <div className="mt-5 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-base leading-relaxed text-[#15130F]/64">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <div className="rounded-[26px] border border-[#E3E6DF] bg-[#15130F] p-7 text-white md:p-9">
              <Database size={26} className="mb-8 text-[#73d99f]" />
              <h2 className="text-3xl font-semibold">ต้องการใช้สิทธิหรือสอบถามเรื่องข้อมูล?</h2>
              <p className="mt-4 text-base leading-relaxed text-white/62">
                ติดต่อทีม KHORN AI เพื่อสอบถามเรื่อง privacy, Google API, การลบข้อมูล หรือการจัดการข้อมูลสำนักงานกฎหมายของคุณ
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="mailto:contact@khorn-ai.com" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#FFFFFF] px-6 text-sm font-bold text-[#15130F] transition hover:bg-[#EEF3EA]">
                  <Mail size={16} />
                  contact@khorn-ai.com
                </a>
                <Link to="/contact" className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-6 text-sm font-bold text-white transition hover:bg-[#FFFFFF] hover:text-[#15130F]">
                  ติดต่อเรา
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </article>
        </section>

        <section className="border-t border-[#E3E6DF] bg-[#FFFFFF] py-12">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 text-sm text-[#5F665E] md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex items-center gap-2">
              <KeyRound size={16} className="text-[#6A7168]" />
              Google API access can be revoked by the user through their Google account settings.
            </div>
            <nav className="flex flex-wrap gap-x-5 gap-y-3 font-bold text-[#15130F]">
              <Link to="/privacy-policy" className="transition hover:text-[#6A7168]">นโยบายความเป็นส่วนตัว</Link>
              <Link to="/business-policy" className="transition hover:text-[#6A7168]">Terms of Service / Business Policy</Link>
              <Link to="/contact" className="transition hover:text-[#6A7168]">ติดต่อ</Link>
            </nav>
          </div>
        </section>
      </main>
    </div>
  );
}
